import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { billingAutoPayAsync, getBillingContractBillsAsync, getContractAsync, getProviderAccountsAsync } from "src/api/contract/contract-api";
import { BillPayStatus, BillingContractBillModel, ContractModel, CtProviderAccount, ScheduleRecentBill } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton, ConfirmModal } from "src/components";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import { useToast } from "src/pages/hooks/toast";
import { Modal } from "src/pages/visitor/visitor-types";
import { getUserDevice, numberToStringWithComma, ViewYmdFormat } from "src/utils/common-util";
import { BILL_SUPPLY_TYPE_MAP } from "src/utils/type-map";
import { isBillPaySuccess } from "../contract-bill-types";
import { useErrorModal } from "src/recoil/errorModal/hook";
import usePostMessage from "src/pages/hooks/post-message";

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 > 청구 내역 상세 화면
 */
const ContractBillDetail = () => {
  const navigate = useNavigate();

  const location = useLocation();

  const device = getUserDevice();

  const { goBack } = useNavigateGoBack();

  const { openErrorModal } = useErrorModal();

  // 토스트
  const { openToast } = useToast();

  const { t } = useTranslation(["bill", "common"]);

  // path variable 계약 id, 청구회차
  const { contractId, billOrder } = useParams();

  // 신청/계약 상세 조회 api
  const { executeAsync: getContract } = useApiOperation(getContractAsync);

  // 청구정보 결제현황 조회 api
  const { executeAsync: getBillingContractBills } = useApiOperation(getBillingContractBillsAsync);

  // 계약 프로바이더 입금계좌 조회 api
  const { executeAsync: getProviderAccounts } = useApiOperation(getProviderAccountsAsync);

  // 이용료 결제 api
  const { executeAsync: billingAutoPay } = useApiOperation(
    billingAutoPayAsync, //
    {
      noHandleError: true,
    },
  );

  // 웹뷰 out
  const { postMessageGoBackNavigation } = usePostMessage();

  // 신청/계약 상세
  const [contract, setContract] = useState<ContractModel | null>(null);

  // 청구정보 결제현황
  const [billingContractBill, setBillingContractBill] = useState<BillingContractBillModel>();

  // 계약 프로바이더 입금계좌 목록
  const [providerAccounts, setProviderAccounts] = useState<CtProviderAccount[]>([]);

  // 경고 모달
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });

  const [confirmModal, setConfirmModal] = useState<Modal>({ isOpen: false });

  // 스케줄 리스트에 rental item이 있는지 확인
  const isBillKey = useMemo(() => {
    let _isBillKey = false;
    if (contract && contract.scheduleList.length > 0) {
      const rentalSchedule = contract?.scheduleList.find((item) => item.supplyType === "RENTAL");
      if (rentalSchedule?.isBillkey) {
        _isBillKey = rentalSchedule.isBillkey;
      }

      return _isBillKey;
    }
  }, [contract]);

  // 임대료 계좌 목록
  const rentAccounts = useMemo(() => {
    if (!providerAccounts || providerAccounts.length === 0) return [];
    return providerAccounts.filter((account) => account.accountType === "RENT_ACCOUNT");
  }, [providerAccounts]);

  const billItem = billingContractBill?.billList?.find((bill) => bill.billOrder === Number(billOrder));

  const pgcode = useMemo(() => {
    return billItem?.originBill?.pgcode || "";
  }, [billItem]);
  // 신청/계약 상세 조회
  const fetchContract = useCallback(
    async (contractId: string) => {
      const { data } = await getContract({ contractId });
      const contract = data?.data || null;
      setContract(contract);
    },
    [getContract],
  );

  // 청구정보 결제현황 조회
  const fetchBillingContractBills = useCallback(
    async (contractId: number | string) => {
      const { data } = await getBillingContractBills({ contractId });
      setBillingContractBill(data?.data);
    },
    [getBillingContractBills],
  );

  // 계약 프로바이더 입금계좌 조회
  const fetchProviderAccounts = useCallback(
    async (contractApplyNumber: string) => {
      const { data } = await getProviderAccounts({
        contractApplyNumber,
        accountType: "RENT_ACCOUNT", // 임대료 계좌
      });
      setProviderAccounts(data?.data?.content || []);
    },
    [getProviderAccounts],
  );

  useEffect(() => {
    if (contractId) {
      // 청구정보 결제현황 조회
      fetchBillingContractBills(contractId);
    }
  }, [contractId, fetchBillingContractBills]);

  useEffect(() => {
    if (contractId) {
      // 신청/계약 상세 조회
      fetchContract(contractId);
    }
  }, [contractId, fetchContract]);

  useEffect(() => {
    if (contract?.contractApplyNumber) {
      // 계약 프로바이더 입금계좌 조회
      fetchProviderAccounts(contract.contractApplyNumber);
    }
  }, [contract, fetchProviderAccounts]);

  // const titleBillDate = moment(billItem?.originBill.billStartTime).format("YYYY년 M월");

  const getPaymentStatus = (billPayStatus: BillPayStatus, paymentsDate: string, receiptTotalAmount: number) => {
    // TODO - paymentsDate 가 없으면 미납?
    if (!paymentsDate) return;

    if (isBillPaySuccess(billPayStatus)) {
      return `${moment(paymentsDate).format(ViewYmdFormat.YYYY_MM_DD)} ${t("billingHistory.paid")}`;
    }

    if (receiptTotalAmount > 0) {
      return `${numberToStringWithComma(receiptTotalAmount)} ${t("billingDetails.received")}`;
    }
    return t("billingHistory.unpaid");
  };

  // 당월 이용료 / 연체료 결제 api
  const billingAutoPayApi = async (id: string) => {
    if (!isBillKey) {
      setAlertModal({ isOpen: true });
      return;
    }

    // api-operation 에서 eCT801 에러에 대한 팝업 노출 작업 함

    try {
      const response = await billingAutoPay({ id });

      if (response.status >= 200 && response.status <= 299) {
        navigate(`/court/payment/result?contractId=${contractId}&response=PAYMENT_SUCCESS&billOrder=${billOrder}`);
      } else {
        // noHandleError=true 라 api-operation 이 throw 하지 않고 _error.response 를 그대로 반환함.
        // 따라서 여기의 response 는 이미 axios 의 .response(status/data 보유) 이므로 .response 를 더 타지 않는다.
        const res: any = response;
        const errorCode: string = res?.data?.meta?.errorCode || "";
        const errorData = res?.data?.meta?.errorData || "";
        const statusCode = Number(res?.status || 0);

        if (errorCode === "eCT801") {
          // 정기결제 진행 중
          openErrorModal({ errorCode, errorMessage: t("billingDetails.autoPayInProgressError"), errorData, statusCode });
        } else if (errorCode === "eCT406") {
          // 청구서가 결제 가능한 상태가 아님 → 공통 에러 모달 노출
          // ErrorModal 은 errorCode 가 있으면 코드 문자열을 노출하므로, 안내문구를 보여주기 위해 errorCode 는 전달하지 않음
          openErrorModal({ errorMessage: res?.data?.meta?.errorMessage || t("billingDetails.generalError"), errorData, statusCode });
        } else if (errorCode === "eCT307") {
          // 자동결제/수납 '이용안함' → 관리자 문의 안내 모달
          // ErrorModal 은 errorCode 가 있으면 코드 문자열을 노출하므로, 안내문구를 보여주기 위해 errorCode 는 전달하지 않음
          openErrorModal({ errorMessage: t("billingDetails.autoPayDisabledError"), statusCode });
        } else if (errorCode) {
          // 그 외 결제 실패 → 실패 결과 페이지 이동
          navigate(`/court/payment/result?contractId=${contractId}&response=PAYMENT_FAILED&billOrder=${billOrder}`);
        } else {
          // errorCode 를 읽지 못한 예외 상황 → 공통 모달로 최소 피드백
          openErrorModal({ errorMessage: t("billingDetails.generalError"), statusCode });
        }
      }
    } catch (error: any) {
      // 응답 자체가 없는 네트워크 에러 등 (billingAutoPay 가 undefined 반환 시 status 접근에서 throw)
      console.error("billingAutoPayApi error", error);
      openErrorModal({ errorMessage: t("billingDetails.generalError") });
    }
  };

  const getBillingDetailList = ({
    billTotalAmount,
    overdueAmount,
    prevSumUnpaidAmount,
    prevSumOverdueAmount,
    billPayStatus,
    overduePayStatus,
    originBillLastReceiptDate,
    overdueLastReceiptDate,
    billSupplyType,
    originReceiptTotalAmount,
    overdueReceiptTotalAmount,
    originBillId,
    overDueId,
  }: {
    billTotalAmount: number;
    overdueAmount: number;
    prevSumUnpaidAmount: number;
    prevSumOverdueAmount: number;
    billPayStatus: BillPayStatus;
    overduePayStatus: BillPayStatus;
    originBillLastReceiptDate: string;
    overdueLastReceiptDate: string;
    billSupplyType: string;
    originReceiptTotalAmount: number;
    overdueReceiptTotalAmount: number;
    originBillId: string;
    overDueId: string;
  }) => {
    return [
      {
        title: t("billingDetails.currentMonthCharge"),
        amount: billTotalAmount,
        paymentStatus: getPaymentStatus(billPayStatus, originBillLastReceiptDate, originReceiptTotalAmount),
        color: isBillPaySuccess(billPayStatus) ? "" : "text-red",
        isVisible: true,
        prevSumOverdueAmount,
        originBillId, // 당월 이용료
      },
      {
        title: t("billingDetails.currentMonthOverdueFee"),
        amount: overdueAmount,
        paymentStatus: getPaymentStatus(overduePayStatus, overdueLastReceiptDate, overdueReceiptTotalAmount),
        color: isBillPaySuccess(overduePayStatus) ? "" : "text-red",
        isVisible: overdueAmount > 0,
        prevSumOverdueAmount,
        overduePayStatus, // 연체료는 자기 상태로 결제 가능 여부 판단 (이용료 상태 billPayStatus 참조 금지)
        isOverdue: true, // 연체료 항목 식별 플래그 (이용료 항목엔 없음)
        overDueId,
      },
      {
        title: t("billingDetails.carryoverUnpaid"),
        amount: prevSumUnpaidAmount,
        paymentStatus: t("billingHistory.unpaid"),
        color: "text-red",
        isVisible: prevSumUnpaidAmount > 0,
        prevSumOverdueAmount,
      },
      {
        title: t("billingDetails.carryoverOverdue"),
        amount: prevSumOverdueAmount,
        paymentStatus: t("billingHistory.unpaid"),
        color: "text-red",
        isVisible: prevSumOverdueAmount > 0,
        prevSumOverdueAmount,
      },
    ]
      .filter((v) => v.isVisible)
      .map((v) => {
        return {
          ...v,
          amount: numberToStringWithComma(v.amount),
        };
      });
  };

  //
  const billingDetailList = useMemo(() => {
    // console.log("billItem", billItem);
    return getBillingDetailList({
      billPayStatus: billItem?.billPayStatus!,
      overduePayStatus: billItem?.overduePayStatus!,
      billTotalAmount: billItem?.billTotalAmount!,
      overdueAmount: billItem?.overdueAmount!,
      prevSumUnpaidAmount: billItem?.prevSumUnpaidAmount!,
      prevSumOverdueAmount: billItem?.prevSumOverdueAmount!,
      originBillLastReceiptDate: billItem?.originBill?.receiptBundle?.lastTrDateTime!,
      overdueLastReceiptDate: billItem?.overdueBill?.receiptBundle?.lastTrDateTime!,
      billSupplyType: BILL_SUPPLY_TYPE_MAP[billItem?.billSupplyType!],
      originReceiptTotalAmount: billItem?.originBill?.receiptBundle?.receiptTotalAmount!,
      overdueReceiptTotalAmount: billItem?.overdueBill?.receiptBundle?.receiptTotalAmount!,
      originBillId: String(billItem?.originBill?.billId),
      overDueId: String(billItem?.overdueBill?.billId),
    });
  }, [billItem]);

  if (!billItem) return null;
  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="CLOSE"
        title={billItem.originBill.billTitle}
        onClickCloseButton={() => {
          if (location.state && location.state === "mypage") {
            navigate(`/court/mypage/contracts/${contractId}`, { state: location.state });
          } else {
            goBack();
          }
          // goBack();
        }}
      />

      <div className="contract-page">
        <h1 className="base-title">{t("billingDetails.monthlyBillingHistory")}</h1>
        <article className="contract-history-montly">
          <section className="contract-main base-section-layout">
            <div className="gray-card">
              <div className="base-section-layout-line">
                <p className="base-section-layout-index">
                  <b className="font18 font-roboto">{t("billingDetails.totalAmountDue")}</b>
                </p>
                <p className="base-section-layout-value">
                  <b className="font18 font-roboto">{numberToStringWithComma(billItem.requestSumAmount)}</b> {t("common:currencyUnit")}
                </p>
              </div>
              <div className="base-section-layout-line">
                <p className="base-section-layout-index">{t("billingDetails.paymentDeadline")}</p>
                <p className="base-section-layout-value">{moment(billItem.originBill.billDueDate).format(ViewYmdFormat.YYYY_MM_DD)} {t("billingDetails.until")}</p>
              </div>
            </div>
            <ul className="pt20 pb30">
              <li>
                <Trans t={t} i18nKey="billingDetails.paymentDeadlineNotice1" components={{ b: <b /> }} />
              </li>
              <li>{t("billingDetails.paymentDeadlineNotice2")}</li>
            </ul>
          </section>

          <section className="base-section-layout top-divider">
            <div className="base-section-layout__container">
              {/* 원청구 */}
              {billingDetailList.map((item, index) => {
                return (
                  <div className="base-section-layout__wrap" key={item.title}>
                    {index === 0 && (
                      <div className="base-section-layout__title-wrap">
                        <h2>{t("billingDetails.billingDetails")}</h2>
                      </div>
                    )}
                    <div className="base-section-layout-line">
                      <p className="base-section-layout-index text-primary3">{item.title}</p>
                      <div className={`base-section-layout-value ${item.color}`}>
                        <p>
                          <b>{item.amount}</b> {t("common:currencyUnit")}
                        </p>
                        <p>{item.paymentStatus}</p>
                      </div>
                    </div>
                    {/* 결제방식이 creditcard 일 때만 노출 (신용카드 결제)*/}
                    {!item.paymentStatus && (contract?.isBillkey || pgcode === "creditcard") && (
                      <div className="mt40">
                        <BaseButton
                          title={t("billingDetails.makePayment")}
                          className="base-btn size-small color-white"
                          onClick={() => {
                            let billId = "";

                            if (item.originBillId) {
                              billId = item.originBillId;
                            }

                            if (item.overDueId) {
                              billId = item.overDueId;
                            }

                            setConfirmModal({ isOpen: true, message: t("billingDetails.paymentConfirmMessage"), payload: String(billId) || "" });
                          }}
                          disabled={item.prevSumOverdueAmount > 0 || (item.isOverdue && item.overduePayStatus !== "PAYMENT_READY") || false}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="base-section-layout__wrap">
                <ul>
                  <li>{t("billingDetails.unpaidReflectNotice")}</li>
                  <li>{t("billingDetails.lateFeeNotice")}</li>
                  <li>{t("billingDetails.detailInfoNotice")}</li>
                </ul>
              </div>
            </div>
          </section>

          <section
            className={`base-section-layout ${
              (!billItem?.overdueBill && Number(billItem?.overdueAmount || 0) > 0) || billItem?.overdueBill ? "top-divider" : ""
            }`}
          >
            <div className="base-section-layout__container">
              {rentAccounts.map((account: CtProviderAccount, index: number) => (
                <div key={index} className="base-section-layout__wrap">
                  {pgcode === "banktransfer" && (
                    <>
                      <div className="base-section-layout__title-wrap">
                        <h2>{t("billingDetails.accountInfo")}</h2>
                      </div>
                      <div className="base-section-layout-line">
                        <p className="base-section-layout-index text-primary3">{t("billingDetails.accountNumber")}</p>
                        <CopyToClipboard text={`${account.bankName} ${account.accountNumber}`}>
                          <p
                            className="base-section-layout-value text-underline cursor-pointer"
                            onClick={() => {
                              openToast({ content: t("billingDetails.accountCopied") });
                            }}
                          >
                            {account.bankName} {account.accountNumber}
                          </p>
                        </CopyToClipboard>
                      </div>
                      <div className="base-section-layout-line">
                        <p className="base-section-layout-index text-primary3">{t("billingDetails.accountHolder")}</p>
                        <p className="base-section-layout-value">{account.accountHolder}</p>
                      </div>
                    </>
                  )}

                  {rentAccounts.length === index + 1 && (
                    <div className="pt20 pb10 d-flex flex-center">
                      {billItem.billSupplyType === "MNT" && (
                        <button
                          type="button"
                          className="base-btn color-white size-small mr20"
                          onClick={() => navigate(`/court/mypage/contracts/${contractId}`)}
                        >
                          {t("billingDetails.billDetail")}
                        </button>
                      )}
                      <button
                        type="button"
                        className="base-btn color-white size-small"
                        onClick={() => navigate(`/court/mypage/contracts/${contractId}`, { state: location.state })}
                      >
                        {t("billingDetails.applicationContractDetails")}
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {rentAccounts.length === 0 && (
                <div className="pt20 pb10 d-flex flex-center">
                  {billItem.billSupplyType === "MNT" && (
                    <button
                      type="button"
                      className="base-btn color-white size-small mr20"
                      onClick={() => navigate(`/court/mypage/contracts/${contractId}`)}
                    >
                      {t("billingDetails.billDetail")}
                    </button>
                  )}
                  <button type="button" className="base-btn color-white size-small" onClick={() => navigate(`/court/mypage/contracts/${contractId}`)}>
                    {t("billingDetails.applicationContractDetails")}
                  </button>
                </div>
              )}
            </div>
          </section>
        </article>
      </div>
      {alertModal.isOpen && (
        <ConfirmModal isOpen={alertModal.isOpen} btnRightTitle={t("common:confirm")} onClick={() => setAlertModal({ isOpen: false })}>
          <div>
            <h2 className=" mb20">{t("billingDetails.registerPaymentMethod")}</h2>
            <p className="text-primary3">
              <Trans t={t} i18nKey="billingDetails.registerCreditCardGuide" components={{ br: <br /> }} />
            </p>
          </div>
        </ConfirmModal>
      )}

      {confirmModal.isOpen && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          btnRightTitle={t("common:confirm")}
          btnLeftTitle={t("common:cancel")}
          onClick={() => {
            billingAutoPayApi(String(confirmModal.payload));
            setConfirmModal({ isOpen: false });
          }}
          onClose={() => setConfirmModal({ isOpen: false })}
        >
          <div>
            <p className="font16">{confirmModal.message}</p>
          </div>
        </ConfirmModal>
      )}
    </>
  );
};

export default ContractBillDetail;
